import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import type { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import {
  getAccessToken,
  isTokenExpired,
  refreshAccessToken,
} from '../../../utils/tokenRefresh';
import { Sentry } from '../../../sentry';

const axiosInstance = axios.create({
  baseURL:
    (window as any)?.env?.REACT_APP_API_URL_AXIOS ||
    process.env.REACT_APP_API_URL_AXIOS,
  withCredentials: true,
});

axiosInstance.interceptors.request.use(
  async (config) => {
    if (
      config.url?.includes('/refresh_token') ||
      config.url?.includes('/impersonate') ||
      config.url?.includes('invitation')
    ) {
      const token = getAccessToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    }

    let token = getAccessToken();

    if (!token || isTokenExpired(token)) {
      const tokenState = !token ? 'missing' : 'expired';
      token = await refreshAccessToken();
      if (!token) {
        Sentry.addBreadcrumb({
          category: 'auth',
          level: 'warning',
          message:
            'Request blocked before dispatch — token refresh returned null',
          data: { url: config.url, method: config.method, tokenState },
        });
        return Promise.reject({
          response: {
            status: 401,
            data: {
              code: 401,
              message: 'Session Expired',
              errors: null,
            },
          },
        });
      }
    }

    config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/refresh_token') &&
      !originalRequest.url?.includes('/internal/v1/mfa/')
    ) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        if (newToken) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        console.error('Retry failed:', refreshError);
      }
    }

    return Promise.reject(error);
  }
);

type QueryArgs =
  | string
  | {
      url: string;
      method?: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      body?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
    };

export const axiosBaseQuery =
  (): BaseQueryFn<QueryArgs, unknown, unknown> => async (args) => {
    const {
      url,
      method = 'GET',
      data,
      body,
      params,
    } = typeof args === 'string' ? { url: args } : args;

    try {
      const result = await axiosInstance({
        url,
        method,
        data: data || body,
        params,
      });
      return { data: result.data };
    } catch (error) {
      const err = error as AxiosError;
      Sentry.addBreadcrumb({
        category: 'http',
        level: 'error',
        message: err.response
          ? `Request failed with HTTP ${err.response.status}`
          : 'Request failed with no HTTP response (network/blocked)',
        data: {
          url,
          method,
          status: err.response?.status ?? null,
          hadResponse: Boolean(err.response),
        },
      });
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
          // Preserve the real error's identity (both plain strings, so Redux
          // state stays serializable). sentry.js reuses these to attribute the
          // event to the true origin instead of the reporter frame.
          name: err.name,
          stack: err.stack,
        },
      };
    }
  };
